import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';

import { conns, connsPublic, resetAuthRedirectGuard } from '@cfg/axiosConfig.tsx';
import { UserProfile } from '@app-types/user.types.ts';
import { ValidationTokenStatus } from '@login/types/login.types.ts';
import { persistor, RootState, AppDispatch } from '@store/store.tsx';
import { resetApp } from '@store/resetAction.ts';
import { resetQueryClient } from '@tools/queryClient.ts';
import { logError, logInfo } from '@tools/LogsHandler.tsx';


interface RegistrationState {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

export interface AuthRelationshipTarget {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  relationship_type_id?: number | null;
  relationship_type_code?: string | null;
  relationship_label?: string | null;
}

export interface AuthRelationshipsResponse {
  items: AuthRelationshipTarget[];
  return_to_original?: AuthRelationshipTarget | null;
}

interface AuthState {
  isAuthenticated: boolean;
  status: 'idle' | 'loading' | 'waiting_code' | 'failed' | 'checking';
  registration: RegistrationState;
  authToken?: ValidationTokenStatus | null;
  error: string | null;
  relationships: AuthRelationshipsResponse | null;
  relationshipsStatus: 'idle' | 'loading' | 'failed';
}

export type CheckSessionError = {
  code: 'unauthenticated' | 'unavailable';
  status?: number;
};

const initialState: AuthState = {
  isAuthenticated: false,
  status: 'checking',
  registration: { status: 'idle', error: null },
  authToken: null,
  error: null,
  relationships: null,
  relationshipsStatus: 'idle',
};

export const selectRegistration = (state: RootState) => (
  state.auth.registration ?? { status: 'idle', error: null }
);
export const selectAuthRelationships = (state: RootState) => state.auth.relationships;
export const selectAuthRelationshipsStatus = (state: RootState) => state.auth.relationshipsStatus;


const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  if (!isAxiosError(error)) {
    return fallback;
  }

  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (detail && typeof detail === 'object' && 'message' in detail) {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }
  return error.message || fallback;
};


export const login = createAsyncThunk<
  UserProfile,
  { username: string; password: string; login_type?: string },
  { rejectValue: string; dispatch: AppDispatch }
>(
  'auth/login',
  async (credentials, { rejectWithValue, dispatch }) => {
    try {
      await connsPublic('auth:login').post(credentials);
      const { data } = await conns<UserProfile>('user:me').get();
      resetAuthRedirectGuard();
      const { loadInitialCart } = await import('./cartSlice.tsx');
      await dispatch(loadInitialCart());
      logInfo('* Reduce - login end: ', data);
      logInfo('Bienvenido');
      return data;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'Error al iniciar sesión'));
    }
  },
);

export const logout = createAsyncThunk('auth/logout', async (_, { dispatch }) => {
  await connsPublic('auth:logout').post();

  persistor.pause();
  await persistor.flush();
  await persistor.purge();

  dispatch(resetApp());

  const { fetchSiteSettings, fetchSiteInfo } = await import('./settingsSlice.tsx');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (dispatch as any)(fetchSiteSettings());
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (dispatch as any)(fetchSiteInfo());
});


type CheckSessionArg = { isPublic?: boolean };

export const checkSession = createAsyncThunk<
  UserProfile,
  CheckSessionArg | void,
  { rejectValue: CheckSessionError; state: RootState }
>('auth/checkSession', async (arg, { rejectWithValue }) => {
  const isPublic: boolean = !!arg?.isPublic;
  let refreshed = false;
  try {
    if (isPublic) {
      const { data: session } = await connsPublic<{ authenticated: boolean; can_refresh: boolean }>('auth:session').get();
      if (!session.authenticated && !session.can_refresh) {
        return rejectWithValue({ code: 'unauthenticated', status: 401 });
      }
      if (!session.authenticated && session.can_refresh) {
        try {
          await connsPublic('auth:refresh').post();
          refreshed = true;
        } catch (refreshError) {
          const status = isAxiosError(refreshError) ? refreshError.response?.status : undefined;
          return rejectWithValue({ code: status === 401 || status === 403 ? 'unauthenticated' : 'unavailable', status });
        }
      }
    }
    const client = isPublic
      ? connsPublic<UserProfile>('user:me')
      : conns<UserProfile>('user:me');
    try {
      const { data } = await client.get();
      resetAuthRedirectGuard();
      return data;
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (!isPublic || refreshed || (status !== 401 && status !== 403)) {
        throw error;
      }

      try {
        await connsPublic('auth:refresh').post();
        const { data } = await client.get();
        resetAuthRedirectGuard();
        return data;
      } catch (refreshError) {
        const refreshStatus = isAxiosError(refreshError)
          ? refreshError.response?.status
          : undefined;
        return rejectWithValue({
          code: refreshStatus === 401 || refreshStatus === 403
            ? 'unauthenticated'
            : 'unavailable',
          status: refreshStatus,
        });
      }
    }
  } catch (error) {
    logError('authSlice - checkSession - error: ', error);
    const status = isAxiosError(error) ? error.response?.status : undefined;
    return rejectWithValue({
      code: status === 401 || status === 403 ? 'unauthenticated' : 'unavailable',
      status,
    });
  }
}, {
  condition: (_, { getState }) => (getState() as RootState).auth.status !== 'loading',
});

export const registerViaInvitation = createAsyncThunk<
  unknown,
  { token: string; email: string; username: string; password: string },
  { rejectValue: string }
>('auth/registerViaInvitation', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await connsPublic('auth:register').post(payload);
    return data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    let apiError = error?.response?.data;
    logInfo('registerViaInvitation - apiError: ', apiError);

    if (apiError?.detail) {
      if (typeof apiError.detail === 'string') {
        return rejectWithValue(apiError.detail);
      }
      apiError = apiError.detail;
    }

    if (Array.isArray(apiError)) {
      const mensajes = apiError.map((e: { msg?: string }) => e.msg ?? 'Error').join(' · ');
      return rejectWithValue(mensajes);
    }

    return rejectWithValue('Error inesperado en el registro');
  }
});

export const fetchUserDataAsync = createAsyncThunk<
  UserProfile,
  { forceUpdate?: boolean },
  { rejectValue: string }
>('auth/fetchUserData', async (_, { rejectWithValue }) => {
  try {
    const { data } = await conns<UserProfile>('user:me').get();
    return data;
  } catch (error) {
    return rejectWithValue(`Error al obtener usuario. error: ${error}`);
  }
});

export const loginWithCodeAsync = createAsyncThunk<
  ValidationTokenStatus,
  Record<string, unknown>,
  { rejectValue: string }
>('auth/loginWithCode', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await connsPublic<ValidationTokenStatus>('auth:code').post(payload);
    return data;
  } catch (error) {
    return rejectWithValue(extractApiErrorMessage(error, 'No se pudo validar el código'));
  }
});

export const selectProfileAsync = createAsyncThunk<
  ValidationTokenStatus,
  { authToken: string; target_user_id: number },
  { rejectValue: string }
>('auth/selectProfile', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await connsPublic<ValidationTokenStatus>('auth:select_profile').post(payload);
    return data;
  } catch (error) {
    return rejectWithValue(extractApiErrorMessage(error, 'No se pudo seleccionar el perfil'));
  }
});

export const fetchMyRelationshipsAsync = createAsyncThunk<
  AuthRelationshipsResponse,
  void,
  { rejectValue: string }
>(
  'auth/fetchMyRelationships',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await conns<AuthRelationshipsResponse>('auth:my_relationships').get();
      return data;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'No se pudieron cargar las relaciones'));
    }
  },
  {
    condition: (_, { getState }) => {
      const auth = (getState() as RootState).auth;
      return auth.relationshipsStatus === 'idle' && auth.relationships === null;
    },
  },
);

export const switchProfileAsync = createAsyncThunk<
  ValidationTokenStatus,
  number,
  { rejectValue: string }
>('auth/switchProfile', async (targetUserId, { rejectWithValue }) => {
  try {
    const { data } = await conns<ValidationTokenStatus>('auth:switch_profile', [targetUserId]).post();
    return data;
  } catch (error) {
    return rejectWithValue(extractApiErrorMessage(error, 'No se pudo cambiar de cuenta'));
  }
});

export const returnToOriginAsync = createAsyncThunk<
  ValidationTokenStatus,
  void,
  { rejectValue: string }
>('auth/returnToOrigin', async (_, { rejectWithValue }) => {
  try {
    const { data } = await conns<ValidationTokenStatus>('auth:return_to_origin').post();
    return data;
  } catch (error) {
    return rejectWithValue(extractApiErrorMessage(error, 'No se pudo volver a la sesión original'));
  }
});


const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuthTokenActionCode(state) {
      if (state.authToken) state.authToken.actionCode = undefined;
    },
    resetAuthTokenError(state) {
      if (state.authToken) state.authToken.error = undefined;
    },
    resetAuthTokenState(state) {
      state.authToken = null;
    },
    resetAuthError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.fulfilled, (state) => {
        resetQueryClient();
        state.isAuthenticated = true;
        state.status = 'idle';
        state.error = null;
        state.relationships = null;
        state.relationshipsStatus = 'idle';
      })
      .addCase(login.rejected, (state, action) => {
        logError('login.rejected - action: ', action);
        state.status = 'failed';
        state.isAuthenticated = false;
        state.error = action.payload as string;
      })
      .addCase(login.pending, (state) => {
        state.error = null;
      });

    builder
      .addCase(checkSession.fulfilled, (state) => {
        state.isAuthenticated = true;
        state.status = 'idle';
        state.error = null;
      })
      .addCase(checkSession.rejected, (state, action) => {
        if (action.payload?.code === 'unauthenticated') {
          resetQueryClient();
          state.isAuthenticated = false;
          state.relationships = null;
          state.relationshipsStatus = 'idle';
        }
        state.status = 'idle';
        state.error = action.payload?.code === 'unavailable'
          ? 'No se pudo comprobar la sesión'
          : null;
      });

    builder
      .addCase(logout.fulfilled, (state) => {
        resetQueryClient();
        state.isAuthenticated = false;
        state.status = 'idle';
        state.relationships = null;
        state.relationshipsStatus = 'idle';
      })
      .addCase(logout.rejected, (state) => {
        logError('-->logout.rejected');
        state.status = 'failed';
      });

    builder
      .addCase(loginWithCodeAsync.fulfilled, (state, action) => {
        const payload = action.payload as ValidationTokenStatus;
        if (payload.user) {
          state.isAuthenticated = true;
          state.status = 'idle';
          state.authToken = null;
          state.relationships = null;
        } else if (payload.guest_verified_at) {
          state.status = 'idle';
          state.authToken = null;
        } else {
          state.status = payload.selectionRequired ? 'idle' : 'waiting_code';
          state.authToken = payload;
        }
      })
      .addCase(loginWithCodeAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loginWithCodeAsync.rejected, (state, action) => {
        state.status = 'failed';
        if (state.authToken) {
          state.authToken.error = action.payload as string;
        }
      });

    builder
      .addCase(selectProfileAsync.fulfilled, (state, action) => {
        const payload = action.payload as ValidationTokenStatus;
        if (payload.user) {
          state.isAuthenticated = true;
          state.status = 'idle';
          state.authToken = null;
          state.relationships = null;
        } else {
          state.status = 'idle';
          state.authToken = payload;
        }
      })
      .addCase(selectProfileAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(selectProfileAsync.rejected, (state, action) => {
        state.status = 'failed';
        if (state.authToken) {
          state.authToken.error = action.payload as string;
        }
      });

    builder
      .addCase(fetchMyRelationshipsAsync.pending, (state) => {
        state.relationshipsStatus = 'loading';
      })
      .addCase(fetchMyRelationshipsAsync.fulfilled, (state, action) => {
        state.relationshipsStatus = 'idle';
        state.relationships = action.payload;
      })
      .addCase(fetchMyRelationshipsAsync.rejected, (state) => {
        state.relationshipsStatus = 'failed';
        state.relationships = null;
      });

    builder
      .addCase(switchProfileAsync.fulfilled, (state) => {
        state.isAuthenticated = true;
        state.status = 'idle';
        state.relationships = null;
      })
      .addCase(switchProfileAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(switchProfileAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });

    builder
      .addCase(returnToOriginAsync.fulfilled, (state) => {
        state.isAuthenticated = true;
        state.status = 'idle';
        state.relationships = null;
      })
      .addCase(returnToOriginAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(returnToOriginAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });

    builder
      .addCase(registerViaInvitation.fulfilled, (state) => {
        state.registration.status = 'succeeded';
        state.registration.error = null;
        logInfo('Tu cuenta ha sido creada con éxito');
      })
      .addCase(registerViaInvitation.pending, (state) => {
        state.registration.status = 'loading';
        state.registration.error = null;
      })
      .addCase(registerViaInvitation.rejected, (state, action) => {
        state.registration.status = 'failed';
        logInfo('registerViaInvitation.rejected: action.payload: ', action.payload);
        state.registration.error = action.payload as string;
      });

    builder
      .addMatcher(
        isAnyOf(login.pending, logout.pending, checkSession.pending),
        (state) => {
          state.status = 'loading';
        },
      )
      .addMatcher(
        isAnyOf(login.rejected, logout.rejected),
        (state) => {
          state.status = 'failed';
        },
      );
  },
});

export const {
  resetAuthTokenActionCode,
  resetAuthTokenError,
  resetAuthTokenState,
  resetAuthError,
} = authSlice.actions;
export default authSlice.reducer;
